import React from 'react';
import { observer } from 'mobx-react';
import { v4 as uuid } from 'uuid';
import {
    EVENT_LEVEL_ATTRIBUTES_TABLE_STATUS_TEXT,
    EVENT_LEVEL_ATTRIBUTES_TABLE_DATE_CREATED_TEXT,
    EVENT_LEVEL_ATTRIBUTES_TABLE_DESCRIPTION_TEXT,
    EVENT_LEVEL_ATTRIBUTES_TABLE_FIELD_TYPE_TEXT,
    EVENT_LEVEL_ATTRIBUTES_TABLE_NAME_TEXT,
    NO_EVENT_LEVEL_ATTRIBUTES_TEXT,
} from './event-level-attributes.texts';
import { EventLevelAttributesVM } from './event-level-attributes.viewmodel';
import { FormattedMessage, useIntl } from 'react-intl';
import { ITheadSort, ListLayout } from '../../../global/tables/table-list-layout';
import { ManageAttributesNavigation } from '../shared/attributes-navigation';
import TableLoadingRow from '../../../global/tables/table-loading-row';
import './event-level-attributes.scss';
import MultiSelectionDropdown from '../../../global/multi-selection-dropdown/multi-selection-dropdown';
import { TGetEventAttributesSortKeys } from '../../../../api/attributes.dao';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import DefaultButton from '../../../global/default-button/default-button';
import { EventAttributesListItem } from './event-attributes-list-item/event-attributes-list-item';

type Props = {
    vm: EventLevelAttributesVM;
};
const ID_FOR_ACTIVE_STATUS = uuid();

export const EventLevelAttributePresentation = observer((props: Props): JSX.Element => {
    const intl = useIntl();

    const { vm } = props;
    const isListEmpty = !vm.isLoading && !vm.eventAttributesList.length;

    const tHeads: ITheadSort<TGetEventAttributesSortKeys>[] = [
        {
            title: intl.formatMessage(EVENT_LEVEL_ATTRIBUTES_TABLE_NAME_TEXT),
            sortField: 'eventAttributeName'
        },
        { title: intl.formatMessage(EVENT_LEVEL_ATTRIBUTES_TABLE_DESCRIPTION_TEXT) },
        { title: intl.formatMessage(EVENT_LEVEL_ATTRIBUTES_TABLE_FIELD_TYPE_TEXT) },
        {
            title: intl.formatMessage(EVENT_LEVEL_ATTRIBUTES_TABLE_DATE_CREATED_TEXT),
            sortField: 'createdAt'
        },
        {
            title: (
                <MultiSelectionDropdown
                    entityName={intl.formatMessage(EVENT_LEVEL_ATTRIBUTES_TABLE_STATUS_TEXT)}
                    withSearch={false}
                    onFetchItems={vm.getEventAttributeStatuses}
                    onApply={activeStatus => vm.handleActiveStatusChange(activeStatus)}
                    savedItems={[]}
                    key={ID_FOR_ACTIVE_STATUS}
                />
            )
        },
    ];

    return (
        <div className='event-level-attributes'>
            <section className='horizontal-row horizontal-row--with-bottom-margin event-tabs account-admin-tabs'>
                <ManageAttributesNavigation />
                <div className='horizontal-row__align-to-row-end'>
                    <div className='buttons-block'>
                        {hasPermission(Permission.MANAGE_EVENT_ATTRIBUTES) &&
                            <DefaultButton
                                modifier={['fat', 'confirm-safe']}
                                text={
                                    <FormattedMessage
                                        id='oe.attributes.newEventAttribute'
                                        defaultMessage='+ New Event Attribute'
                                        description='Button label'
                                    />
                                }
                                onClick={vm.openCreateEventAttributeForm.bind(vm)}
                            />
                        }
                    </div>
                </div>
            </section>
            <section className='fixed-list-layout'>
                <div>
                    <ListLayout
                        sortOptions={{
                            theads: tHeads,
                            sortRows: vm.setSortParams,
                            sortField: vm.getSortField(),
                            sortOrder: vm.getSortOrder(),
                        }}
                        isDisabled={isListEmpty}>
                        {vm.isLoading && <TableLoadingRow colSpan={tHeads.length} />}
                        {isListEmpty &&
                            <tr>
                                <td className='table-loading' colSpan={tHeads.length}>
                                    <FormattedMessage {...NO_EVENT_LEVEL_ATTRIBUTES_TEXT} />
                                </td>
                            </tr>
                        }
                        {!vm.isLoading && vm.eventAttributesList.map(item => {
                            return <EventAttributesListItem
                                key={item.eventAttributeId}
                                item={item}
                                onEdit={vm.openEditEventAttributeForm.bind(vm)}
                            />;
                        })}
                    </ListLayout>
                </div>
            </section>
        </div>
    );
});
